import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, ButtonGroup, Card, CardBody, Col, Input, Label, Row } from 'reactstrap';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/dist/sweetalert2.min.css';
import erorSound from '../../assets/audio/alert-error.wav';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import { getTrainJourneys, trainLoadTerminal, undoTrainJourneyLoad } from '../devir-teslim/api';
import useFormatMessage from '../../utils/useFormatMessage';

// Terminal Programları > Tren Yükleme: scan a VIN to load it onto an open train journey — the scan-based equivalent
// of the Tren Seferleri web panel's "Yükleme listesi" action (both call the same backend, TrainJourneyCars
// action=LOAD; every terminal action needs a web/Excel equivalent and vice versa, owner rule 2026-11-02).
// Mirrors GemiYuklemeTerminal.js exactly, substituting train journeys/endpoints.
const JOURNEY_KEY = 'tren-yukleme-terminal-journey';

const TrenYuklemeTerminal = () => {
    const { t } = useFormatMessage();
    const { hasRole } = useAuth(43); // Sevkiyat İşlemleri - Tren Planlama (tren_sefer_write)
    const [journeys, setJourneys] = useState([]);
    const [journeyId, setJourneyId] = useState(() => {
        const saved = localStorage.getItem(JOURNEY_KEY);
        return saved ? Number(saved) : null;
    });
    const [vin, setVin] = useState('');
    const [busy, setBusy] = useState(false);
    const [undoMode, setUndoMode] = useState(false);
    const inputRef = useRef(null);

    // the terminal's own site (set at login, same as every other terminal screen) — this terminal can only load
    // cars onto a journey whose load point is this same warehouse; it has no business touching a journey loading
    // somewhere else entirely (owner rule, 2026-11-02, after a Haydarpaşa scan put a Samandıra car on a Morocco sailing).
    const warehouseId = (() => {
        try { return parseInt(Buffer.from(localStorage.getItem('warehouse') || '', 'base64').toString('ascii'), 10) || 0; }
        catch { return 0; }
    })();

    useEffect(() => {
        getTrainJourneys(true).then((md) => {
            const filtered = (md.journeys || []).filter((j) =>
                !j.cancelled && j.status !== 'TAMAMLANDI' && j.load_warehouse_id === warehouseId);
            setJourneys(filtered);
            // a journey id persisted from an earlier session may no longer be a valid, currently-offered option
            // (departed, completed, or belongs to another warehouse) — don't silently keep sending it to the
            // backend just because it's not visibly selected in the (now-populated) dropdown.
            setJourneyId((current) => {
                if (current != null && !filtered.some((j) => j.id === current)) {
                    localStorage.removeItem(JOURNEY_KEY);
                    return null;
                }
                return current;
            });
        }).catch(() => setJourneys([]));
        // eslint-disable-next-line
    }, []);

    useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, [journeyId]);

    const selectJourney = (id) => {
        setJourneyId(id);
        localStorage.setItem(JOURNEY_KEY, String(id));
    };

    const submit = async (value) => {
        const chassisNo = (value || '').trim().toUpperCase();
        if (!hasRole('tren_sefer_write')) {
            toast.error(t('Bu işlemi yapmaya yetkiniz yoktur.'));
            return;
        }
        if (!journeyId) {
            toast.error(t('Lütfen bir sefer seçiniz.'));
            return;
        }
        if (chassisNo.length !== 17) {
            toast.error(t('17 karakterli şase numarası giriniz'));
            return;
        }
        if (busy) return;
        setBusy(true);
        try {
            const res = undoMode
                ? await undoTrainJourneyLoad(journeyId, chassisNo)
                : await trainLoadTerminal(journeyId, chassisNo, warehouseId);
            await Swal.fire({
                icon: 'success',
                html: `<div style="font-size: 24px;"><b>${undoMode ? t('Geri alındı') : t('Yüklendi')}</b>${res.handover_no ? `<br/><span style="font-size: 18px;">${res.handover_no}</span>` : ''}</div>`,
                timer: 2500, showConfirmButton: false, position: 'center', allowOutsideClick: false,
                customClass: { popup: 'swal2-popup-large' },
            });
            setVin('');
        } catch (e) {
            if (navigator.vibrate) navigator.vibrate(500);
            const errorSound = document.getElementById('errorSound');
            if (errorSound) errorSound.play();
            const error = e?.response?.data?.error || t('Beklenmedik bir hata oluştu');
            await Swal.fire({
                icon: 'error', title: t('Hata'), html: `<div style="font-size: 24px;">${error}</div>`,
                confirmButtonText: t('Tamam'), allowOutsideClick: false, allowEscapeKey: false, background: '#fff5f5',
                customClass: { popup: 'swal2-popup-large' },
            });
            setVin('');
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.focus();
        }
    };

    return (
        <div>
            <audio id="errorSound" src={erorSound} preload="auto"></audio>
            <Breadcrumb current={t('Tren Yükleme')} />
            <Card><CardBody>
                <Row>
                    <Col md="6">
                        <Label>{t('Sefer')}</Label>
                        <Input type="select" value={journeyId || ''} onChange={(e) => selectJourney(Number(e.target.value) || null)}>
                            <option value="">— {t('sefer seçiniz')} —</option>
                            {journeys.map((j) => (
                                <option key={j.id} value={j.id}>
                                    {j.journey_no} · {j.train_service_name} · {j.load_location} → {j.unload_location}
                                </option>
                            ))}
                        </Input>
                        {journeys.length === 0 && <div className="text-muted small mt-1">{t('Bu depodan yüklenen açık bir sefer yok.')}</div>}
                    </Col>
                </Row>
                <Row className="mt-3">
                    <Col md="6">
                        <Label>{t('Mod')}</Label>
                        <div>
                            <ButtonGroup>
                                <Button color={!undoMode ? 'primary' : 'secondary'} active={!undoMode} onClick={() => setUndoMode(false)}>{t('Yükle')}</Button>
                                <Button color={undoMode ? 'danger' : 'secondary'} active={undoMode} onClick={() => setUndoMode(true)}>{t('Geri al')}</Button>
                            </ButtonGroup>
                        </div>
                    </Col>
                </Row>
                <Row className="mt-3">
                    <Col md="6">
                        <Label>{t('Şase')}</Label>
                        <Input innerRef={inputRef} value={vin} disabled={!journeyId || busy}
                            placeholder={t('Okutunuz veya yazınız')}
                            onChange={(e) => setVin(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') submit(vin); }} />
                    </Col>
                </Row>
            </CardBody></Card>
        </div>
    );
};

export default TrenYuklemeTerminal;
